import type {
  ConditionsConfig_ConditionConfig,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { ConditionsDisplay_Visibility } from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import type { CandidateStore } from "../../../../../stores/automation/candidate.store";
import * as T from "../../../../../stores/automation/mergers/trigger";
import * as A from "../../../../../stores/automation/mergers/actions";
import {
  INPUT_APPLICATION_ID,
  TriggerLabel,
} from "../../../../../utils/constants";
import { useRootStore } from "../../../../../stores/hooks";
import {
  findInputAndPick,
  pickValueConfigString,
} from "../../../../../utils/inputs";
import { ConditionsEditor } from "./ConditionsEditor";
import type { ActionCandidate } from "../../../../../types/candidate";
import { isTruthy } from "remeda";
import { translateIfPresent } from "../../../../../locales/i18n";

const isRequired = (action?: ActionCandidate): boolean =>
  action?.description?.conditionsDisplay?.required === true;

const getTitle = (action?: ActionCandidate): string =>
  isTruthy(action?.description?.conditionsDisplay?.label)
    ? translateIfPresent(
        `condition.${action?.description.conditionsDisplay?.label ?? ""}`,
        action?.description.conditionsDisplay?.label,
      )
    : t("general.condition.other");

const getSubTitle = (action?: ActionCandidate): string =>
  isTruthy(action?.description?.conditionsDisplay?.subLabel)
    ? translateIfPresent(
        `condition.${action?.description.conditionsDisplay?.subLabel ?? ""}`,
        action?.description.conditionsDisplay?.subLabel,
      )
    : t("trigger-section.condition-description");

const isConditionsValid = (action?: ActionCandidate): boolean => {
  if (action?.description?.conditionsDisplay?.required === true) {
    return (action?.conditions?.group ?? []).length > 0;
  }
  return true;
};

export const ConditionsForm = observer<{
  candidate: CandidateStore;
  integrations: IntegrationDescription[];
  action?: ActionCandidate;
  isSubmitted?: boolean;
}>(({ candidate, integrations, action, isSubmitted }) => {
  const { maxTriggerConditions } = useRootStore().featureFlags.limits;
  const { trigger } = candidate.self;
  const actionGroupType =
    candidate.currentActionGroup?.config.oneofKind ?? "actions";
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>();

  useEffect(() => {
    const applicationId = findInputAndPick(
      trigger.inputs,
      INPUT_APPLICATION_ID,
      pickValueConfigString,
    );

    if (
      applicationId !== selectedApplicationId &&
      candidate.self.trigger.inputs.length > 0 &&
      candidate.self.trigger.conditions?.group.length === 0 &&
      candidate.self.trigger.description.conditionFields.length > 0 &&
      candidate.self.trigger.triggerReference?.triggerId === "matches-condition"
    ) {
      candidate.setTrigger(T.mergeInitialCondition());
      setSelectedApplicationId(applicationId);
    }
  }, [candidate.self.trigger]);

  const onAddCondition = (): void => {
    if (action !== undefined) {
      candidate.setActions(
        [A.mergeActionDefaultCondition(action.hash)],
        actionGroupType,
      );
    } else {
      candidate.setTrigger(T.mergeDefaultCondition());
    }
  };
  const onChangeCondition = (
    condition: ConditionsConfig_ConditionConfig,
    index: number,
  ): void => {
    if (action === undefined) {
      candidate.setTrigger(T.mergeUpdatedCondition(condition, index));
    } else {
      candidate.setActions(
        [A.mergeUpdatedCondition(action.hash, condition, index)],
        actionGroupType,
      );
    }
  };
  const onDeleteCondition = (index: number): void => {
    if (action === undefined) {
      candidate.setTrigger(T.ejectCondition(index));
    } else {
      candidate.setActions(
        [A.ejectCondition(action.hash, index)],
        actionGroupType,
      );
    }
  };

  const onSwitchOperator = (): void => {
    action !== undefined
      ? candidate.setActions(
          [A.switchConditionOperator(action.hash)],
          actionGroupType,
        )
      : candidate.switchTriggerConditionOperator();
  };

  const allowDelete =
    action === undefined &&
    trigger.triggerReference?.triggerId === TriggerLabel.matchesCondition
      ? Boolean(trigger.conditions && trigger.conditions.group.length > 1)
      : true;

  const isAllowAdd =
    (action !== undefined &&
      action.description.conditionFields.length > 0 &&
      (action.conditions?.group.length === undefined ||
        action.conditions?.group.length < maxTriggerConditions)) ||
    // @ts-expect-error
    trigger.conditions?.group.length < maxTriggerConditions;

  // conditionFields can be present purely as sources for downstream
  // conditions, so visibility (not their presence) gates the block.
  if (
    action !== undefined &&
    action.description.conditionsDisplay?.visibility !==
      ConditionsDisplay_Visibility.DISPLAY
  ) {
    return null;
  }

  return (
    <ConditionsEditor
      conditions={action === undefined ? trigger.conditions : action.conditions}
      conditionFields={
        action === undefined
          ? trigger.description.conditionFields
          : action.description.conditionFields
      }
      candidate={candidate}
      integrations={integrations}
      action={action}
      title={getTitle(action)}
      subtitle={getSubTitle(action)}
      showRequiredMark={isRequired(action)}
      showRequiredError={isSubmitted === true && !isConditionsValid(action)}
      allowDelete={allowDelete}
      isAllowAdd={isAllowAdd}
      onAdd={onAddCondition}
      onChange={onChangeCondition}
      onDelete={onDeleteCondition}
      onSwitchOperator={onSwitchOperator}
    />
  );
});
